// Portfolio calculations — pure, decimal-safe (cents-based for money).
// Keep these calculations matched to the Excel parity tests in tests/unit/portfolio.test.ts.

export interface HoldingValuationInput {
  units: number;       // can be fractional
  currentPrice: number; // AUD per unit
  costBase: number;     // AUD total
}
export interface HoldingValuation {
  currentValue: number; // AUD
  unrealisedGainLoss: number; // AUD
  unrealisedReturnPct: number; // 0..1
}

export function valueHolding(h: HoldingValuationInput): HoldingValuation {
  const currentValue = round2(h.units * h.currentPrice);
  const unrealisedGainLoss = round2(currentValue - h.costBase);
  const unrealisedReturnPct = h.costBase > 0 ? round4(unrealisedGainLoss / h.costBase) : 0;
  return { currentValue, unrealisedGainLoss, unrealisedReturnPct };
}

export interface AccountValueInput {
  cashBalance: number;
  holdings: HoldingValuationInput[];
}

export function valueAccount(a: AccountValueInput): number {
  const holdingsTotal = a.holdings.reduce((sum, h) => sum + valueHolding(h).currentValue, 0);
  return round2(a.cashBalance + holdingsTotal);
}

export function totalNetWorth(accounts: AccountValueInput[]): number {
  return round2(accounts.reduce((sum, a) => sum + valueAccount(a), 0));
}

export interface AllocationSlice {
  bucket: string;
  value: number;
  pct: number; // 0..1
}

export function assetAllocation(
  holdings: Array<HoldingValuationInput & { bucket: string }>,
): AllocationSlice[] {
  const totals = new Map<string, number>();
  for (const h of holdings) {
    const value = valueHolding(h).currentValue;
    totals.set(h.bucket, (totals.get(h.bucket) ?? 0) + value);
  }
  const grand = [...totals.values()].reduce((a, b) => a + b, 0);
  return [...totals.entries()]
    .map(([bucket, value]) => ({
      bucket,
      value: round2(value),
      pct: grand > 0 ? round4(value / grand) : 0,
    }))
    .sort((a, b) => b.value - a.value);
}

export function adviserFee(fum: number, feePct: number): number {
  return round2(fum * feePct);
}

export function brokerageFee(tradeValue: number, brokeragePct: number, minimum: number): number {
  return round2(Math.max(tradeValue * brokeragePct, minimum));
}

/* ---------------------------------------------------------------------------
 * Asset-class + geography labelling (Phase 6 — PerformanceTab UI).
 *
 * The seed/fixture asset-class codes (`equity_au`, `equity_intl`, `property`,
 * `fi`, `cash`, `alt`) are friendly enough for an adviser screen but ugly in
 * a chart legend; `assetClassLabel` returns the human label. `geographyForHolding`
 * folds asset classes into a small geography bucket — pure on (assetClass, code)
 * so it is unit-testable without a DB.
 *
 * Geography buckets — kept short on purpose so the pie reads at a glance:
 *   • Australia      — equity_au, AU property, AU FI, AU cash
 *   • International  — equity_intl, intl ETFs (recognised by code prefix /
 *                      well-known global ETFs traded on the ASX)
 *   • Cash           — cash sub-class
 * -------------------------------------------------------------------------*/

const ASSET_CLASS_LABELS: Record<string, string> = {
  equity_au: 'Australian shares',
  equity_intl: 'International shares',
  property: 'Property',
  fi: 'Fixed income',
  cash: 'Cash',
  alt: 'Alternatives',
};

export function assetClassLabel(klass: string | null | undefined): string {
  if (!klass) return 'Unclassified';
  return ASSET_CLASS_LABELS[klass] ?? klass;
}

/**
 * Known international ETFs / managed funds quoted in AUD on the ASX. The
 * underlying assetClass should already be `equity_intl`, but this catches
 * codes mis-labelled `equity_au` at seed time and the handful of fixed-income
 * ETFs whose underlying exposure is global.
 */
const INTL_ETF_CODES = new Set<string>([
  'VTS', 'VEU', 'VGS', 'VGAD', 'IVV', 'IOO', 'IWLD', 'IEM', 'IEU', 'IJP',
  'NDQ', 'QUAL', 'WXOZ', 'WDIV', 'HACK', 'ROBO', 'ETHI', 'FAIR',
  'PLA0002AU', // Platinum International (managed fund)
]);

/** AUD-denominated AU-domiciled fixed-income / property / cash ETFs. */
const AU_INCOME_CODES = new Set<string>([
  'IAF', 'VAF', 'VGB', 'VAP', 'BOND', 'AAA', 'BILL', 'XARO',
]);

export type GeographyBucket = 'Australia' | 'International' | 'Cash';

export function geographyForHolding(klass: string | null | undefined, code: string): GeographyBucket {
  const upper = (code ?? '').toUpperCase();
  if (klass === 'cash') return 'Cash';
  if (klass === 'equity_intl') return 'International';
  if (INTL_ETF_CODES.has(upper)) return 'International';
  if (AU_INCOME_CODES.has(upper)) return 'Australia';
  // equity_au / property / fi (AU hybrids) / alt with no intl marker default to Australia.
  return 'Australia';
}

/**
 * Geography allocation across a set of holdings. Symmetric to `assetAllocation`
 * but bucketed by country instead of asset class. Cash held as account cash
 * (not a holding) should be passed in as an extra row with `assetClass: "cash"`
 * by the caller — the snapshot service does that — so a household with $50k
 * cash + $50k AU shares correctly reports 50% Australia / 50% Cash.
 */
export function geographyAllocation(
  rows: Array<HoldingValuationInput & { code: string; assetClass: string | null }>,
): AllocationSlice[] {
  return assetAllocation(
    rows.map((r) => ({
      bucket: geographyForHolding(r.assetClass, r.code),
      units: r.units,
      currentPrice: r.currentPrice,
      costBase: r.costBase,
    })),
  );
}

/* ---- helpers ---- */
function round2(n: number) { return Math.round(n * 100) / 100; }
function round4(n: number) { return Math.round(n * 10000) / 10000; }

/* ---------------------------------------------------------------------------
 * Target-allocation validation (round 3 #11). Targets are decimal fractions
 * keyed by raw asset-class code; advisers expect them to sum to 100%.
 * -------------------------------------------------------------------------*/
export interface TargetAllocationCheck {
  sumPct: number;          // 0..100+
  isComplete: boolean;     // within 0.5% of 100
  hasInvalid: boolean;     // any non-finite / out-of-range value present
  setCount: number;        // how many buckets have a target
}

export function validateTargetAllocation(
  targets: Record<string, string | number | null | undefined>,
): TargetAllocationCheck {
  let sum = 0;
  let setCount = 0;
  let hasInvalid = false;
  for (const v of Object.values(targets)) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0 || n > 1) { hasInvalid = true; continue; }
    sum += n;
    setCount += 1;
  }
  const sumPct = round2(sum * 100);
  return { sumPct, isComplete: Math.abs(sumPct - 100) <= 0.5, hasInvalid, setCount };
}
